// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { AdvisoryDebtAccount } from '@shared/data-access/types/brokerage-types';
import { groupBy } from '@shared/ui-kit';
import {
  getLatestInstallmentDate,
  resolveInstitutionLabel,
} from './debt-amortizations';

export type CreditLinesTableRow = {
  id: string;
  bankName: string;
  productName: string;
  limit: number | null | undefined;
  disposed: number | null | undefined;
  available: number | null | undefined;
  usageRatio: number | null;
  maturityDate: string | null;
};

export type GroupedCreditLinesTableRow = CreditLinesTableRow & {
  subRows: CreditLinesTableRow[];
};

export const buildCreditLinesTableRows = (
  accounts: AdvisoryDebtAccount[] = [],
  institutionNames: Map<string, string> = new Map(),
): CreditLinesTableRow[] =>
  accounts.flatMap(account =>
    (account.credit_lines ?? []).map(creditLine => {
      const limit = creditLine.limit_amount ?? 0;
      const disposed = creditLine.disposed ?? 0;
      const available =
        creditLine.available ?? (limit > 0 ? limit - disposed : null);
      const maturityDate =
        creditLine.expiration_date ??
        getLatestInstallmentDate(creditLine.amortizations);

      return {
        id: creditLine.provider_uuid,
        bankName: resolveInstitutionLabel(account.institution_id, institutionNames),
        productName: creditLine.name ?? creditLine.product_type ?? '-',
        limit: creditLine.limit_amount,
        disposed: creditLine.disposed,
        available,
        usageRatio: limit > 0 ? disposed / limit : null,
        maturityDate,
      };
    }),
  );

export const buildGroupedCreditLinesTableRows = (
  rows: CreditLinesTableRow[] = [],
): GroupedCreditLinesTableRow[] => {
  if (rows.length === 0) {
    return [];
  }

  const banks = groupBy(rows, 'bankName');

  return Array.from(banks.entries())
    .sort(([bankA], [bankB]) => bankA.localeCompare(bankB))
    .map(([bankName, subRows]) => {
      const limit = subRows.reduce((acc, row) => acc + (row.limit ?? 0), 0);

      const disposed = subRows.reduce((acc, row) => acc + (row.disposed ?? 0), 0);

      const available = subRows.reduce((acc, row) => acc + (row.available ?? 0), 0);

      return {
        id: `bank-${bankName}`,
        bankName,
        productName: '',
        limit,
        disposed,
        available,
        usageRatio: limit > 0 ? disposed / limit : null,
        maturityDate: null,
        subRows,
      };
    });
};
